import type {ReactElement} from 'react';

import {css} from '@emotion/react';
import React from 'react';
import {Link} from 'react-router-dom';

import type {DialogProps} from './Dialog';

import Hamburger from '../assets/icons/Hamburger';
import useViewer from '../hooks/useViewer';
import {linkToApp} from '../lib/url/linkToApp';
import {linkToMarketing} from '../lib/url/linkToMarketing';
import {useDialog} from '../store/DialogContext';
import {useAppTheme, useColor, useComponent} from '../styles';
import LanguageMenu from './LanguageMenu';
import ProfileImg from './ProfileImg';

export type BaseHeaderProps = {
  mobileMenuDrawer: DialogProps['children'];
  navLinks?: ReactElement[];
  showViewerActions?: boolean;
};

/**
 * The main header component for the app, displaying navigation links, brand logo, language menu, and viewer actions.
 *
 * @component
 * @param {BaseHeaderProps} props - Props for the BaseHeader component.
 * @returns {JSX.Element} The rendered header element.
 */
const BaseHeader = (props: BaseHeaderProps): JSX.Element => {
  const {navLinks, mobileMenuDrawer, showViewerActions} = props;
  const {spacings, shadows, breakpoints} = useAppTheme();
  const {page, buttons} = useComponent();
  const {palette} = useColor();
  const {viewer, loading} = useViewer();
  const {openDialog} = useDialog();

  const pageBreakpoint = breakpoints.lg;

  const headerCss = css([
    {
      display: 'flex',
      justifyContent: 'center',
      height: spacings.topNav.sm.height,
      position: 'fixed',
      width: '100%',
      top: 0,
      boxShadow: shadows.m,
      zIndex: 10,
      backgroundColor: palette.background.white.defaultColor,
      [pageBreakpoint]: {
        backdropFilter: 'blur(5px)',
        height: spacings.topNav.lg.height,
        backgroundColor: `${palette.background.white.defaultColor}DD`, // Hex color + Opacity
      },
    },
  ]);

  const headerPaddingsCss =
    !viewer.id &&
    css({
      [pageBreakpoint]: {
        paddingLeft: spacings.px20,
        paddingRight: spacings.px20,
      },
      [breakpoints.xl]: {
        paddingLeft: spacings.px20,
        paddingRight: spacings.px20,
      },
    });

  const headerContentCss = css([
    page.lg,
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerPaddingsCss,
  ]);

  const brandCss = css({
    height: spacings.px32,
    [pageBreakpoint]: {
      height: spacings.px48,
    },
  });

  const navContainerCss = css({
    display: 'none',
    [pageBreakpoint]: {
      display: 'flex',
      alignItems: 'center',
      gap: spacings.px30,
    },
  });

  const navCss = css({
    display: 'flex',
    gap: spacings.px32,
  });

  const guestButtonsCss = css({
    display: 'flex',
    gap: spacings.px15,
  });

  const hamburgerButtonCss = css({
    fontSize: spacings.px24,
    [pageBreakpoint]: {
      display: 'none',
    },
  });

  const handleHamburgerClick = () => {
    openDialog(mobileMenuDrawer);
  };

  const renderNav = () => {
    if (!navLinks) return;

    const renderNavLinks = () => {
      return navLinks.map((NavLink, index) => {
        return <li key={index}>{NavLink}</li>;
      });
    };

    return (
      <nav>
        <ul css={navCss}>{renderNavLinks()}</ul>
      </nav>
    );
  };

  const profileImageContainer = css({
    height: spacings.px48,
    width: spacings.px48,
    borderRadius: spacings.px48,
    overflow: 'hidden',
  });

  const renderViewerActions = () => {
    if (loading || !showViewerActions) return;

    if (viewer.id) {
      return (
        <Link
          css={profileImageContainer}
          to={linkToApp('/')}
        >
          <ProfileImg src={viewer.profile?.imageUrl} />
        </Link>
      );
    }

    return (
      <div css={guestButtonsCss}>
        <Link
          css={buttons.secondarySubdued.l}
          to={linkToApp('/login')}
        >
          Log in
        </Link>
        <Link
          css={buttons.accent.l}
          to={linkToApp('/signup')}
        >
          Join free today
        </Link>
      </div>
    );
  };

  return (
    <header css={headerCss}>
      <div css={headerContentCss}>
        <Link to={linkToMarketing('/')}>
          <img
            alt="alliz"
            css={brandCss}
            src="/static/images/brand-logo.svg"
          />
        </Link>
        <section css={navContainerCss}>
          {renderNav()}
          <LanguageMenu />
          {renderViewerActions()}
        </section>
        <button
          css={hamburgerButtonCss}
          onClick={handleHamburgerClick}
        >
          <Hamburger />
        </button>
      </div>
    </header>
  );
};

export default BaseHeader;
